import { useEffect, useState } from "react";
import { MagnifyingGlass, MapPin, Crosshair, MapTrifold } from "@phosphor-icons/react";
import { formatPlaceDistance, placeService } from "../../services/placeService";
import PlacesMap from "../map/PlacesMap";
import LocationPermissionBanner from "../location/LocationPermissionBanner";
import { useGeolocation } from "../../hooks/useGeolocation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export default function PlacePicker({
  radiusKm = 25,
  value = null,
  onChange,
  onPositionChange,
}) {
  const { position, locating, denied, usingSaved, locate } = useGeolocation();
  const [places, setPlaces] = useState([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [mapOpen, setMapOpen] = useState(false);

  function selectPlace(place) {
    onChange?.(place);
  }

  useEffect(() => {
    if (position) {
      onPositionChange?.(position);
    }
  }, [position, onPositionChange]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 220);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!position) return;
    let active = true;
    setLoadingPlaces(true);
    setLoadError("");
    placeService
      .listNearby({
        latitude: position.lat,
        longitude: position.lng,
        radiusKm,
        search: debouncedQuery,
        forMap: true,
      })
      .then((list) => {
        if (!active) return;
        setPlaces(list);
        if (list.length === 1 && !value && !debouncedQuery) {
          onChange?.(list[0]);
        }
      })
      .catch((err) => {
        if (!active) return;
        setLoadError(err.message || "No se pudieron cargar los lugares");
        setPlaces([]);
      })
      .finally(() => {
        if (active) setLoadingPlaces(false);
      });
    return () => {
      active = false;
    };
  }, [position, radiusKm, debouncedQuery]);

  const mapPlacesCount = places.filter((p) => p.lat != null && p.lng != null).length;

  return (
    <div className="space-y-3" data-testid="place-picker">
      <div className="flex items-start justify-between gap-3">
        <div>
          <label className="zx-label">Hospedaje</label>
          <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[rgba(255,255,255,0.4)]">
            Obligatorio. Elige un hotel dentro del radio de tu plan ({radiusKm} km).
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <button
            type="button"
            onClick={() => setMapOpen(true)}
            disabled={!position || locating}
            className="flex items-center gap-1 rounded-full border border-[rgba(184,50,90,0.45)] bg-[rgba(139,30,63,0.18)] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[#e9b5c5] transition-colors hover:bg-[rgba(139,30,63,0.32)] hover:text-[#f5f5f5] disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="place-picker-map-button"
          >
            <MapTrifold size={12} weight="thin" />
            Ver mapa
          </button>
          <button
            type="button"
            onClick={() => locate({ preferSaved: false })}
            className="flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#e9b5c5] hover:text-[#f5f5f5]"
            data-testid="place-picker-locate"
          >
            <Crosshair size={12} weight="thin" />
            {locating ? "Ubicando…" : "Actualizar"}
          </button>
        </div>
      </div>

      {denied && (
        <LocationPermissionBanner
          onRetry={() => locate({ preferSaved: false })}
          locating={locating}
          usingSaved={usingSaved && !!position}
        />
      )}

      {position && (
        <>
          <div className="relative">
            <MagnifyingGlass
              size={14}
              weight="thin"
              className="absolute left-0 top-1/2 -translate-y-1/2"
            />
            <input
              className="zx-input pl-6"
              placeholder="Buscar por nombre, ciudad o dirección…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="place-picker-search"
            />
          </div>

          <div
            className="zx-card max-h-72 overflow-auto p-2"
            data-testid="place-picker-results"
          >
            {loadingPlaces ? (
              <p className="p-3 text-[10px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.5)]">
                Cargando lugares…
              </p>
            ) : loadError ? (
              <p className="p-3 text-[10px] uppercase tracking-[0.2em] text-[#e9b5c5]">
                {loadError}
              </p>
            ) : places.length === 0 ? (
              <p className="p-3 text-[10px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.5)]">
                {debouncedQuery
                  ? "Sin coincidencias para tu búsqueda."
                  : "No hay lugares en tu radio de acción."}
              </p>
            ) : (
              places.map((place) => {
                const selected = value?.uuid === place.uuid;
                const distanceLabel = formatPlaceDistance(place);
                const locationLine = [
                  place.streetAddress,
                  place.addressLocality,
                  place.addressCountry,
                ]
                  .filter(Boolean)
                  .join(", ");

                return (
                  <button
                    key={place.uuid}
                    type="button"
                    onClick={() => selectPlace(place)}
                    className={[
                      "flex w-full items-start justify-between gap-3 rounded-lg px-3 py-3 text-left transition-colors",
                      selected
                        ? "bg-[rgba(139,30,63,0.28)] ring-1 ring-[rgba(184,50,90,0.45)]"
                        : "hover:bg-[rgba(255,255,255,0.04)]",
                    ].join(" ")}
                    data-testid={`place-option-${place.uuid}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-[#f5f5f5]">{place.name}</p>
                      {locationLine && (
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-[rgba(255,255,255,0.55)]">
                          <MapPin size={11} weight="thin" />
                          <span className="truncate">{locationLine}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {distanceLabel && (
                        <span className="zx-tag">{distanceLabel}</span>
                      )}
                      {selected && <span className="zx-tag accent">Seleccionado</span>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}

      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent
          className="fixed bottom-3 left-3 right-3 top-3 z-50 flex translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden border-[rgba(255,255,255,0.12)] bg-[#0a0a0a] p-0 text-[#f5f5f5] duration-0 data-[state=closed]:animate-none data-[state=open]:animate-none sm:bottom-6 sm:left-6 sm:right-6 sm:top-6 sm:rounded-2xl md:bottom-10 md:left-[max(1rem,calc(50%-28rem))] md:right-[max(1rem,calc(50%-28rem))] md:top-10"
          data-testid="place-picker-map-dialog"
        >
          <DialogHeader className="border-b border-[rgba(255,255,255,0.08)] px-5 py-4 text-left">
            <DialogTitle className="font-serif text-2xl font-normal tracking-tight text-[#f5f5f5]">
              Hospedajes en tu radio
            </DialogTitle>
            <DialogDescription className="text-[10px] uppercase tracking-[0.22em] text-[rgba(255,255,255,0.45)]">
              {radiusKm} km · {mapPlacesCount} en mapa
              {value?.name ? ` · Seleccionado: ${value.name}` : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="relative min-h-0 flex-1">
            {mapOpen && position && (
              <PlacesMap
                key={`${position.lat}-${position.lng}-${places.length}`}
                active={mapOpen}
                userPosition={position}
                places={places}
                radiusKm={radiusKm}
                selectedUuid={value?.uuid ?? null}
                onSelectPlace={(place) => {
                  selectPlace(place);
                  setMapOpen(false);
                }}
              />
            )}
          </div>

          <p className="border-t border-[rgba(255,255,255,0.08)] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.4)]">
            Punto blanco: tu ubicación · Círculo: radio de acción · Toca un marcador y
            pulsa «Escoger este lugar» en el tooltip
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
